import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Grid3X3,
  LayoutPanelLeft,
  Package,
  Printer,
  ReceiptText,
  SlidersHorizontal,
  Tag,
  UtensilsCrossed,
} from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import AdminProducts from '../AdminProducts';
import AdminSalesCatalog from '../manager/AdminSalesCatalog';
import AdminArticleArrangement from './AdminArticleArrangement';
import AdminArticleGroups from './AdminArticleGroups';
import AdminArticleOptions from './AdminArticleOptions';
import AdminArticleScreens from './AdminArticleScreens';

const STUDIO_TABS = [
  {
    id: 'menus',
    label: 'Menüs',
    description: 'Menütypen, Partner-Sichtbarkeit und Gruppierung',
    icon: UtensilsCrossed,
  },
  {
    id: 'products',
    label: 'Artikel',
    description: 'Stammdaten, Preise und Basisartikel',
    icon: Package,
  },
  {
    id: 'groups',
    label: 'Gruppen',
    description: 'Artikelgruppen und Farben',
    icon: Tag,
  },
  {
    id: 'options',
    label: 'Optionen',
    description: 'Modifier, Extras und Zuordnungen',
    icon: SlidersHorizontal,
  },
  {
    id: 'layout',
    label: 'Bonieren',
    description: 'Left bar, Reihenfolge und Bestellschein',
    icon: LayoutPanelLeft,
  },
];

const EXTRA_LINKS = [
  {
    title: 'Druckergruppen',
    description: 'Produktion, Theke und Bondruck sauber zuordnen.',
    href: '/admin/articles/print-groups',
    icon: Printer,
  },
  {
    title: 'Preisstufen',
    description: 'Spezielle Preislogiken und Varianten pflegen.',
    href: '/admin/articles/pricing',
    icon: ReceiptText,
  },
  {
    title: 'Ganggruppen',
    description: 'Ablauf in der Küche und Service-Reihenfolge strukturieren.',
    href: '/admin/articles/courses',
    icon: Grid3X3,
  },
];

export default function AdminArticleStudio({ initialTab = 'menus' }) {
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const activeConfig = STUDIO_TABS.find((tab) => tab.id === activeTab) || STUDIO_TABS[0];

  return (
    <div className="space-y-6 p-6">
      <div className="space-y-3">
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-slate-400">Admin / Artikel</p>
        <h1 className="text-3xl font-bold tracking-tight text-[#132d39]">Artikel- und Menü-Studio</h1>
        <p className="max-w-4xl text-sm text-slate-500">
          Hier konfigurierst du die komplette Verkaufsstruktur: Menüs mit Typen und Kanälen, Gruppen, Artikel,
          Optionen und die Darstellung im POS-Bonieren. Das Ziel ist eine einzige Quelle für Kassa, Webshop,
          Bestellmonitor und Partner.
        </p>
      </div>

      <div className="grid gap-3 xl:grid-cols-5">
        {STUDIO_TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`rounded-[28px] border p-4 text-left transition-colors ${
                isActive
                  ? 'border-[#132d39] bg-[#132d39] text-white shadow-sm'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center gap-3">
                <span
                  className={`inline-flex h-11 w-11 items-center justify-center rounded-2xl ${
                    isActive ? 'bg-white/10 text-white' : 'bg-slate-100 text-[#132d39]'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="font-semibold">{tab.label}</p>
                  <p className={`mt-1 text-xs ${isActive ? 'text-white/70' : 'text-slate-500'}`}>
                    {tab.description}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-5">
          <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Aktiver Bereich</p>
              <h2 className="mt-1 text-2xl font-bold text-[#132d39]">{activeConfig.label}</h2>
              <p className="mt-1 text-sm text-slate-500">{activeConfig.description}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {EXTRA_LINKS.map((link) => {
                const Icon = link.icon;
                return (
                  <Button key={link.href} variant="outline" asChild className="rounded-2xl">
                    <Link to={link.href}>
                      <Icon className="mr-2 h-4 w-4" />
                      {link.title}
                    </Link>
                  </Button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {activeTab === 'menus' ? <AdminSalesCatalog embedded /> : null}
      {activeTab === 'products' ? <AdminProducts embedded /> : null}
      {activeTab === 'groups' ? <AdminArticleGroups embedded /> : null}
      {activeTab === 'options' ? <AdminArticleOptions embedded /> : null}
      {activeTab === 'layout' ? (
        <div className="space-y-6">
          <AdminArticleArrangement embedded />
          <AdminArticleScreens embedded />
        </div>
      ) : null}
    </div>
  );
}
